import React, { useRef, useState } from "react";
import axios from "axios";
import { API, apiErr } from "@/lib/api";
import { Star, Camera, Loader2, Fingerprint, Send } from "lucide-react";
import { toast } from "sonner";

const Stars = ({ n, onPick, size = "h-5 w-5" }) => (
  <span className="inline-flex gap-0.5" data-testid="stars">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" onClick={onPick ? () => onPick(i) : undefined} data-testid={onPick ? `star-${i}` : undefined} className={onPick ? "cursor-pointer" : "cursor-default"}>
        <Star className={size} fill={i <= n ? "#F59E0B" : "none"} stroke={i <= n ? "#F59E0B" : "#475569"} />
      </button>
    ))}
  </span>
);

export const PassportFeedback = ({ lotId, feedback, rating, onPosted }) => {
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const fd = new FormData();
    fd.append("rating", stars); fd.append("comment", comment);
    if (photo) fd.append("photo", photo);
    try {
      const { data } = await axios.post(`${API}/commons/passport/${lotId}/feedback`, fd);
      toast.success(`Thanks — your rating is anchored (${data.anchor.slice(0, 12)}…) and joined the passport chain`);
      setComment(""); setPhoto(null); if (fileRef.current) fileRef.current.value = "";
      onPosted();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="qs-panel p-5" data-testid="passport-feedback">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Shopper feedback · {rating.count} rating(s){rating.with_photo ? ` · ${rating.with_photo} photo(s)` : ""}</div>
        {rating.avg != null && <div className="flex items-center gap-2 font-mono text-sm" data-testid="rating-avg"><Stars n={Math.round(rating.avg)} size="h-3.5 w-3.5" /> {rating.avg}/5</div>}
      </div>
      <form onSubmit={submit} className="print:hidden grid sm:grid-cols-[auto_1fr_auto_auto] gap-2 items-center bg-[#050A15] border border-white/5 p-3" data-testid="feedback-form">
        <Stars n={stars} onPick={setStars} />
        <input data-testid="feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={300} placeholder="How was it? (optional, 300 chars)"
          className="bg-transparent border border-white/10 px-2 py-1.5 text-xs outline-none focus:border-[#F59E0B] w-full" />
        <label className="flex items-center gap-1 text-[11px] text-slate-400 border border-white/10 px-2 py-1.5 cursor-pointer hover:border-[#00FFFF]" title="Attach a photo (jpg/png/webp, 5 MB)">
          <Camera className="h-3.5 w-3.5" /> {photo ? photo.name.slice(0, 14) : "photo"}
          <input ref={fileRef} data-testid="feedback-photo" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
        </label>
        <button data-testid="feedback-submit" disabled={busy} className="qs-cta bg-[#F59E0B] text-[#050A15] font-semibold px-3 py-1.5 text-xs flex items-center gap-1">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Rate
        </button>
      </form>
      <p className="text-[10px] text-slate-600 mt-2 print:hidden">No account needed · max 3 per day per device · every rating (and the photo's SHA-256) is hash-anchored into this lot's chain.</p>
      <div className="mt-3 space-y-2">
        {feedback.map((f) => (
          <div key={f.id} className="flex gap-3 border-t border-white/5 pt-2 text-xs" data-testid="feedback-item">
            {f.photo_path && <img src={`${API}/commons/feedback/${f.id}/photo`} alt="shopper photo" className="h-14 w-14 object-cover border border-white/10 shrink-0" loading="lazy" data-testid="feedback-photo-img" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2"><Stars n={f.rating} size="h-3 w-3" /><span className="text-slate-500">{new Date(f.created_at).toLocaleDateString()}</span></div>
              {f.comment && <div className="text-slate-300 mt-0.5">{f.comment}</div>}
              <div className="text-[10px] font-mono text-slate-600 truncate flex items-center gap-1"><Fingerprint className="h-3 w-3" /> {f.anchor}</div>
            </div>
          </div>
        ))}
        {!feedback.length && <p className="text-xs text-slate-600">Be the first shopper to rate this lot.</p>}
      </div>
    </div>
  );
};
